// The quiet note when the account holds something this web version can't show (UpdateViews.swift: UpdateGate,
// UpdateSuggestionSection). On the web an update is a reload: the page loads the newest version. Once per visit the
// note says it, then goes; Settings keeps saying it.

import { useEffect, useState } from "react";
import type { Suggestion } from "../core/featureLevel";
import { t } from "../i18n";
import { useWindow } from "./context";

export function updateNote(suggestion: Suggestion): string {
  switch (suggestion.kind) {
    case "version": return t("Todolix {version} shows everything in your account.", { version: suggestion.version });
    case "newer": return t("A newer version of Todolix shows everything in your account.");
    case "notYet": return t("Your account holds things this version can’t show yet.");
  }
}

export function UpdateNote() {
  const { state } = useWindow();
  const suggestion = state.updateSuggestion;
  const [shown, setShown] = useState<"waiting" | "showing" | "done">("waiting");
  useEffect(() => {
    if (!suggestion || shown !== "waiting") return;
    const show = setTimeout(() => setShown("showing"), 1500);
    return () => clearTimeout(show);
  }, [suggestion, shown]);
  useEffect(() => {
    if (shown !== "showing") return;
    const hide = setTimeout(() => setShown("done"), 8000);
    return () => clearTimeout(hide);
  }, [shown]);
  if (!suggestion || shown !== "showing") return null;
  return (
    <div className="update-note" role="status" onClick={() => setShown("done")}>
      <span>{updateNote(suggestion)}</span>
      {suggestion.kind !== "notYet" && <button className="link" onClick={() => location.reload()}>{t("Reload")}</button>}
    </div>
  );
}

/** In Settings, for as long as a newer version would show more. */
export function UpdateSettings() {
  const { state } = useWindow();
  const suggestion = state.updateSuggestion;
  if (!suggestion) return null;
  return (
    <section className="settings-group" aria-label={t("Update")}>
      <h3>{t("Update")}</h3>
      <div className="settings-row">
        <div>
          <b>{updateNote(suggestion)}</b>
          <span>{suggestion.kind === "notYet"
            ? t("It is kept, and this version works as usual.")
            : t("Something in your account was made with a newer version. It is kept, and this version works as usual; a newer one shows everything.")}</span>
        </div>
        {suggestion.kind !== "notYet" && <button className="outline-button small" onClick={() => location.reload()}>{t("Reload")}</button>}
      </div>
    </section>
  );
}
